import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHelmet from '@/components/feature/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { featuredComics, latestUpdates, categories, faqItems } from '@/mocks/comics';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <>
      <SEOHelmet
        title="羞羞漫画 - 正版成人漫画平台官方入口 | 海量高清漫画免费在线阅读"
        description="羞羞漫画是深受用户喜爱的成人漫画阅读平台，提供海量正版漫画资源、每日更新内容、高清无水印阅读体验。注册即享专属福利，畅读羞羞漫画全站精品内容。"
        keywords="羞羞漫画,羞羞漫画官网入口,羞羞漫画免费阅读,羞羞漫画最新更新,羞羞漫画正版平台,成人漫画推荐,漫画在线阅读"
        canonicalUrl="https://www.xiuxiumanhua.com"
        ogImage="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20comic%20reading%20platform%20social%20media%20preview%2C%20warm%20red%20and%20gold%20accents%2C%20stylized%20manga%20art%2C%20clean%20modern%20layout%2C%20professional%20branding&width=1200&height=630&seq=home-og&orientation=landscape"
        faqData={faqItems}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Artistic%20abstract%20Chinese%20ink%20wash%20painting%20style%2C%20flowing%20red%20and%20black%20ink%20swirls%2C%20elegant%20warm%20gradient%20background%2C%20soft%20golden%20accents%2C%20Japanese%20and%20Chinese%20manga%20aesthetic%2C%20stylized%20brush%20strokes%2C%20dramatic%20atmospheric%20lighting%2C%20no%20text%2C%20abstract%20composition&width=1920&height=900&seq=hero-bg-2026&orientation=landscape"
              alt="羞羞漫画背景"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
                海量正版漫画
                <br />
                <span className="text-primary-400">随时随地轻松追更</span>
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                羞羞漫画汇聚5000+正版授权漫画，每日更新100+话，高清无水印阅读体验，让每一位漫画爱好者都能尽情享受阅读乐趣。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/romance"
                  className="px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  立即阅读
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-white/10 text-white font-medium rounded-full hover:bg-white/20 backdrop-blur-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1.5">
              <div className="w-1 h-2.5 bg-white/50 rounded-full animate-bounce" />
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">5000+</p>
                <p className="text-xs text-foreground-500 mt-1">正版漫画</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">500万+</p>
                <p className="text-xs text-foreground-500 mt-1">注册用户</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-heading font-bold text-accent-500">100+</p>
                <p className="text-xs text-foreground-500 mt-1">每日更新</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-heading font-bold text-accent-500">800+</p>
                <p className="text-xs text-foreground-500 mt-1">签约作者</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                分类浏览
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                八大分类，精准定位你的阅读偏好，发现更多精彩漫画
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group p-5 bg-background-100 rounded-lg hover:bg-primary-50 transition-colors duration-200 text-center cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-full group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 mb-1">{cat.name}</h3>
                  <p className="text-xs text-foreground-500">{cat.count}部作品</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Recommendations */}
        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500">编辑精选，不容错过的品质佳作</p>
              </div>
              <Link
                to="/category/romance"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-product-shop>
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-14 md:py-16 bg-background-50" id="latest">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500">每日更新，追更不停歇</p>
              </div>
              <Link
                to="/category/romance"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-product-shop>
              {latestUpdates.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="normal" />
              ))}
            </div>
          </div>
        </section>

        {/* Platform Features */}
        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                为什么选择羞羞漫画
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                四大核心优势，打造极致的漫画阅读体验
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-shield-check-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">正版授权</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">所有漫画均获得正版授权，尊重创作者权益</p>
              </div>
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                  <i className="ri-calendar-check-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">每日更新</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">热门作品保持高频更新，追更不停歇</p>
              </div>
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-hd-line text-2xl text-secondary-500"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">高清画质</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">全站高清无水印，每一格细节都清晰呈现</p>
              </div>
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-device-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">多端适配</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">手机、平板、电脑随心切换，随时随地阅读</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于羞羞漫画，你想知道的都在这里</p>
            </div>
            <div className="space-y-3">
              {faqItems.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full px-5 py-4 flex items-center justify-between text-left cursor-pointer hover:bg-background-200/70 transition-colors"
                    onClick={() => toggleFaq(index)}
                  >
                    <span className="text-sm font-medium text-foreground-800 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20background%20with%20flowing%20red%20and%20dark%20ink%20waves%2C%20Chinese%20painting%20aesthetic%2C%20soft%20golden%20sparkles%2C%20elegant%20atmospheric%20mood%2C%20dramatic%20yet%20refined%20composition%2C%20no%20text&width=1920&height=600&seq=cta-bg-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-900/90 to-foreground-900/70" />
          </div>
          <div className="relative z-10 max-w-2xl mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              加入羞羞漫画，开启阅读之旅
            </h2>
            <p className="text-sm text-white/70 mb-8 max-w-md mx-auto">
              注册即享3天VIP体验，畅读全站精品内容，随时随地轻松追更
            </p>
            <button className="px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              免费注册
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}